import { hasFlag } from '@ofmos/shared/roles';
import { type ReactNode, useMemo } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router';
import { usePendingCount } from '../../../data/hooks';
import { LargeTitle, Skeleton, useIsDesktop } from '../../../ui';
import { useSession } from '../../auth/useSession';
import { HomeRedirect } from '../../HomeRedirect';
import { PageHeader } from '../../shell/PageHeader';
import { PaneHost } from '../../shell/PaneHost';
import { formatMoney } from '../clients/clientModel';
import {
  changesInto,
  DataAgeLine,
  MarkHandledAction,
  OwnerMenu,
  PauseAction,
  SKELETON_DELAY_MS,
  StaleTelegramNotice,
  useDelayed,
  useHealthHistory,
  useOwnerMenu,
  useReplyHandled,
} from '../shared';
import { useTeamTasks } from './Commitments';
import { type Focus, FocusClients, type FocusKind } from './FocusClients';
import { KpiRow, type KpiTrend } from './KpiRow';
import {
  actionLine,
  activeClients,
  behindNote,
  dayKeyOf,
  daySubtitle,
  type FocusRow,
  focusLists,
  focusRowsOf,
  type HistoryColour,
  historySince,
  historyTrend,
  moneyBuckets,
  moneyNote,
  nameOf,
  needsAttentionCount,
  onboardingSummary,
  taskLists,
} from './model';
import { TodayColumn } from './TodayColumn';
import { type DashboardData, useDashboardData } from './useDashboardData';

/**
 * BEN'S DASHBOARD, AND SINCE THE REDESIGN THE START PAGE (register P1, H1, H2, H3; brief
 * `REDESIGN-BRIEF-FE-DASHBOARD-2026-10-04.md`; mockup `dashboard-v2-side.html`).
 *
 * Top to bottom: the large title with the day, one action line, the KPI row, and two columns -
 * Clients to focus on, and Today on the right. Home is gone (H1); what it carried that the
 * people need came over into Today as quiet lines (H2), the rest stayed behind (H3).
 *
 * THE PAGE STANDS STILL ON THE DESKTOP AND THE COLUMNS SCROLL (owner finding F5, Q-DB-12):
 * the pane host is the screen's scrollport under the bar, its content a column exactly as tall
 * as the host, and the two columns take what the title, the line and the tiles leave, each a
 * bounded scroll region (`DashboardColumn`). Below the breakpoint nothing is bounded and the
 * document scrolls, the focus list first and Today after it (P6: the phone keeps working).
 */

/**
 * THE INDEX ROUTE (H1): this page where the fulfillment model resolves (an agency object and
 * enrollments carrying `health`), and otherwise exactly the fallback Home used - the first
 * object's list, or the "No Objects" state - so the platform stays generic.
 */
export function StartPage() {
  const data = useDashboardData();
  const waiting = useDelayed(!data.modelKnown, SKELETON_DELAY_MS);
  if (!data.modelKnown) return waiting ? <PageSkeleton /> : null;
  if (!data.resolves) return <HomeRedirect />;
  return <DashboardPage data={data} />;
}

/** `/fulfillment/dashboard` keeps working for sent links and the sidebar row, with its query. */
export function DashboardRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/', search: location.search }} replace />;
}

function PageSkeleton() {
  return (
    <div className="space-y-3 py-8" aria-busy="true">
      <Skeleton className="h-8 w-40" label="Loading the dashboard" />
      <Skeleton shape="block" />
    </div>
  );
}

/** The tile's delta, line and names, from the history alone (FE-DB-12). */
function trendOf(
  history: ReturnType<typeof useHealthHistory>['data'],
  since: string,
  colour: HistoryColour,
): KpiTrend | null {
  const trend = historyTrend(history, since, colour);
  if (trend === null || history === undefined) return null;
  return { ...trend, entered: changesInto(history.changes, colour) };
}

function DashboardPage({ data }: { readonly data: DashboardData }) {
  const isDesktop = useIsDesktop();
  const session = useSession();
  const today = dayKeyOf(new Date());
  const [params, setParams] = useSearchParams();
  const focus: Focus = params.get('focus') === 'yellow' ? 'yellow' : 'red';
  const chooseFocus = (next: Focus) => {
    const updated = new URLSearchParams(params);
    if (next === 'red') updated.delete('focus');
    else updated.set('focus', next);
    setParams(updated, { replace: true });
  };

  /** One hidden set for the page: "Mark as handled" and "Pause…" both hide the row at once. */
  const handled = useReplyHandled();
  const { hidden } = handled;

  /**
   * THE OWNER MENU COUNTS THE FOCUS COLUMN'S ROWS (common brief: an option's count is the rows
   * it shows here), every list of it for All; the page then filters by the chosen owner.
   */
  const clients = useMemo(
    () => activeClients(data.enrollments, data.agencies, data.hasStatusField),
    [data.enrollments, data.agencies, data.hasStatusField],
  );
  const everyone = useMemo(
    () => focusLists(clients, 'all', data.reasonRules, today, hidden),
    [clients, data.reasonRules, today, hidden],
  );
  const owned = useMemo(
    () =>
      focusRowsOf(everyone).map((row) => ({
        ownerId: row.agency?.csmId ?? null,
        ownerName: row.agency?.csmName ?? null,
      })),
    [everyone],
  );
  const menu = useOwnerMenu(owned);
  const owner = menu.filter;

  const lists = useMemo(
    () => focusLists(clients, owner, data.reasonRules, today, hidden),
    [clients, owner, data.reasonRules, today, hidden],
  );
  /** The tiles count the rows on screen; their deltas come from the history alone (FE-DB-12). */
  const needsAttention = needsAttentionCount(lists);
  const onboarding = useMemo(
    () => onboardingSummary(data.enrollments, data.agencies, owner, today),
    [data.enrollments, data.agencies, owner, today],
  );
  const money = useMemo(() => moneyBuckets(data.payments, today), [data.payments, today]);
  const teamTasks = useTeamTasks(today);
  const tasks = useMemo(
    () => taskLists(teamTasks.entries, data.agencyObject?.id, data.agencies, owner, today),
    [teamTasks.entries, data.agencyObject?.id, data.agencies, owner, today],
  );
  const since = historySince(today);
  const history = useHealthHistory(since, owner);
  const mayDecide = session.data != null && hasFlag(session.data.permissions, 'PENDING_DECIDE');
  const pending = usePendingCount();

  const tierOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const enrollment of data.enrollments) {
      if (enrollment.agencyId === null || enrollment.tier === null) continue;
      if (!map.has(enrollment.agencyId) || enrollment.status === 'Active')
        map.set(enrollment.agencyId, enrollment.tier);
    }
    return map;
  }, [data.enrollments]);

  const line = actionLine(lists.waiting.length, lists.goneQuiet.length);
  const paymentList =
    data.paymentObject === undefined ? undefined : `/objects/${data.paymentObject.id}`;
  const loadingLists = useDelayed(data.isPending, SKELETON_DELAY_MS);
  const agencyObjectId = data.agencyObject?.id;
  const pausedField = data.agencyFields.get('paused_until');

  /** "Mark as handled" on a Waiting row; "Pause…" on every row of the column (Q-DB-2). */
  const actions = (row: FocusRow, kind: FocusKind): ReactNode => {
    const agency = row.agency;
    if (agency === undefined || agencyObjectId === undefined) return null;
    const target = { id: agency.id, name: nameOf(row) };
    return (
      <>
        {kind === 'waiting' && handled.available && (
          <MarkHandledAction onMark={() => void handled.mark(target)} />
        )}
        <PauseAction
          agencyObjectId={agencyObjectId}
          agency={target}
          field={pausedField}
          current={agency.pausedUntil}
          onPaused={handled.hide}
          onResumed={handled.show}
        />
      </>
    );
  };

  let columns: ReactNode;
  if (data.isPending) {
    columns = loadingLists ? <PageSkeleton /> : null;
  } else {
    const focusColumn = (
      <FocusClients
        focus={focus}
        lists={lists}
        today={today}
        age={<DataAgeLine source="telegram" />}
        notice={<StaleTelegramNotice />}
        actions={actions}
      />
    );
    const todayColumn = (
      <TodayColumn
        today={today}
        tasks={tasks}
        {...(data.moneyResolves && data.canReadMoney && paymentList !== undefined
          ? {
              money: {
                buckets: money,
                listHref: paymentList,
                packageOf: (agencyId: string | null) =>
                  agencyId === null ? null : (tierOf.get(agencyId) ?? null),
              },
            }
          : {})}
        onboarding={{
          rows: onboarding.rows,
          intakesToReview: data.intakeNeedsReview,
          age: <DataAgeLine source="intakes" />,
        }}
        {...(mayDecide ? { pending: pending.data ?? 0 } : {})}
      />
    );
    columns = isDesktop ? (
      <div
        className="mt-10 grid min-h-0 flex-1 auto-rows-[minmax(0,1fr)] grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] gap-12"
        data-dashboard-columns=""
      >
        {focusColumn}
        {todayColumn}
      </div>
    ) : (
      <div className="mt-8 space-y-10">
        {focusColumn}
        {todayColumn}
      </div>
    );
  }

  return (
    <div className="md:flex md:h-(--page-viewport) md:min-h-0 md:flex-col">
      <PageHeader
        title="Dashboard"
        largeTitle
        actions={<OwnerMenu menu={menu} context="toolbar" />}
      />
      <PaneHost host="fulfillment-dashboard" className="pb-6">
        <div className="md:flex md:min-h-0 md:flex-1 md:flex-col md:pt-4">
          {isDesktop && <LargeTitle title="Dashboard" subtitle={daySubtitle()} />}
          {line !== null && !data.isPending && (
            <p className="mt-2 text-sm text-ink" data-action-line="">
              Start with the <span className="font-semibold">{String(line.count)}</span> {line.rest}
            </p>
          )}
          {!data.isPending && (
            <KpiRow
              className="mt-6"
              focus={focus}
              onFocus={chooseFocus}
              needsAttention={needsAttention}
              needsAttentionTrend={trendOf(history.data, since, 'red')}
              keepAnEye={lists.yellow.length}
              keepAnEyeTrend={trendOf(history.data, since, 'yellow')}
              onboarding={{ total: onboarding.total, note: behindNote(onboarding) }}
              {...(data.moneyResolves && paymentList !== undefined
                ? {
                    money: {
                      value: data.canReadMoney
                        ? money.overdueTotal === null
                          ? '0'
                          : formatMoney(money.overdueTotal)
                        : null,
                      note: data.canReadMoney ? moneyNote(money) : null,
                      to: paymentList,
                    },
                  }
                : {})}
            />
          )}
          {columns}
        </div>
      </PaneHost>
    </div>
  );
}
